import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { getAdminStats } from '../../api/client';
import { LayoutDashboard, MessageSquare, Users, Settings, LogOut, Edit3, Bell, UserCheck, Mail, Mic2 } from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ newEnquiries: 0, newMessages: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch {
        // Stats will show defaults
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const totalNew = stats.newEnquiries + stats.newMessages;

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: totalNew },
    { path: '/admin/contacts', label: 'Contacts & Leads', icon: UserCheck, badge: stats.totalContacts || 0 },
    { path: '/admin/enquiries', label: 'Enquiries', icon: Users, badge: stats.newEnquiries },
    { path: '/admin/messages', label: 'Messages', icon: MessageSquare, badge: stats.newMessages },
    { path: '/admin/editor', label: 'Visual Editor', icon: Edit3 },
    { path: '/admin/speakers', label: 'Speakers', icon: Mic2 },
    { path: '/admin/live-editor', label: 'Live Editor', icon: Edit3, highlight: true },
    { path: '/admin/notifications', label: 'Notifications', icon: Mail },
    { path: '/admin/settings', label: 'Advanced Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900" data-testid="admin-header-title">ASEAN Business Forum Admin</h1>
            <div className="flex items-center gap-2">
              {admin?.picture && (
                <img src={admin.picture} alt="" className="w-7 h-7 rounded-full" />
              )}
              <span className="text-sm text-gray-500">{admin?.name || admin?.email}</span>
              {admin?.role === 'super_admin' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  Super Admin
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {totalNew > 0 && (
              <Link to="/admin/enquiries" className="relative p-2 hover:bg-gray-100 rounded-full">
                <Bell size={20} className="text-gray-600" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {totalNew}
                </span>
              </Link>
            )}
            <Link to="/" target="_blank" className="text-sm text-green-600 hover:text-green-700">
              View Website
            </Link>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    item.highlight && !isActive
                      ? 'bg-green-600 text-white hover:bg-green-700 font-medium'
                      : isActive
                        ? 'bg-green-50 text-green-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    {item.label}
                  </div>
                  {item.badge > 0 && (
                    <span className={`text-xs font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center ${
                      item.highlight ? 'bg-white text-green-600' : 'bg-red-500 text-white'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
